import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import moment from "moment";
import { notification } from "antd";
import Lottie from "lottie-react";

import { getCredits, transcribeInput } from "../service";
import { getDecodedToken } from "../../../utils/localStorage";
import { getB2cIdWithFallbacks } from "../../../utils/b2cHelper";
import BottomSheetWrapper, {
  BottomSheetWrapperRef,
} from "../../../commonComponents/BottomSheetWrapper";

import { ReactComponent as Pause } from "../../../assets/images/ddx-audio-pause.svg";
import { ReactComponent as Resume } from "../../../assets/images/ddx-audio-resume.svg";
import { ReactComponent as Accept } from "../../../assets/images/ddx-audio-accept.svg";
import { ReactComponent as Delete } from "../../../assets/images/Delete.svg";
import { ReactComponent as Close } from "../../../assets/images/close.svg";
import { ReactComponent as CloseOutline } from "../../../assets/images/close-outline.svg";
import { ReactComponent as Mic } from "../../../assets/images/mic.svg";
import { ReactComponent as MicOutline } from "../../../assets/images/mic-outline.svg";
import { ReactComponent as MicWhiteFilled } from "../../../assets/images/mic-white-filled.svg";
import DDxInputSelectorBg from "../../../assets/images/ddx-input-selector-bg.webp";
import * as DDxSend from "../../../assets/animations/ddx-send.json";
import * as DDxMic from "../../../assets/animations/ddx-mic.json";
import * as Chakra from "../../../assets/animations/tatva_ai_chakra.json";
import VoiceWaveVisualizer from "./VoiceWaveVisualizer";
import Button from "../../home/components/Button";
import { trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

const ShimmerLine = () => {
  return (
    <div className="relative w-full h-4 overflow-hidden bg-gray-200 rounded-md">
      <motion.div
        className="absolute top-0 left-0 w-2/3 h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
      />
    </div>
  );
};

type DDxInputFieldProps = {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  onPressSend: (prompt: string | null) => void;
  inputMode: "audio" | "text" | "converting" | null;
  setInputMode: React.Dispatch<
    React.SetStateAction<"audio" | "text" | "converting" | null>
  >;
  isEditMode?: boolean;
  cancelEditMode?: () => void;
  toggleCreditExpiredBottomSheet?: (action: "open" | "close") => void;
};

const DDxInputField: React.FC<DDxInputFieldProps> = ({
  value,
  setValue,
  onPressSend,
  inputMode,
  setInputMode,
  isEditMode = false,
  cancelEditMode = () => {},
  toggleCreditExpiredBottomSheet = () => {},
}) => {
  const tokenData: any = getDecodedToken();
  const doctorDetails = tokenData?.user || {};

  const [tempValue, setTempValue] = useState<any>(null);

  // const fileSheetRef = useRef<BottomSheetWrapperRef>(null);
  const editConfirmationSheetRef = useRef<BottomSheetWrapperRef>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const canUserGenerateDDx = async (): Promise<boolean> => {
    const b2c_id = await getB2cIdWithFallbacks();
    const res = await getCredits(b2c_id);
    if (res?.status === 200) {
      if (res?.data?.body?.ddx_credit > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        setAudioBlob(null);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  const startRecording = async (): Promise<void> => {
    if (!(await canUserGenerateDDx())) {
      toggleCreditExpiredBottomSheet("open");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/mp4",
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: "audio/mp4" });
        setAudioBlob(audioBlob);
        // setInputMode("converting");
        chunksRef.current = [];
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);

      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert(
        "Unable to access microphone. Please ensure you have granted the necessary permissions."
      );
    }
  };

  const stopRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      setIsPaused(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  };

  const togglePause = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerIntervalRef.current = setInterval(() => {
          setTimer((prev) => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  };

  const formatTime = (seconds: number): string => {
    return moment.utc(seconds * 1000).format("mm:ss");
  };

  const convertAudioToText = async () => {
    const formData = new FormData();
    if (!!audioBlob)
      formData.append("file", audioBlob, `audio-${moment().unix()}.mp4`);
    if (!!doctorDetails) {
      formData.append("doctor_specialty", doctorDetails?.specialityTxt);
    }

    const res = await transcribeInput(formData);
    if (res?.status === 200) {
      setTimer(0);
      if (res?.data?.success) {
        setValue(value + " " + res?.data?.input);
        setInputMode("text");
      } else {
        notification.error({
          duration: 5,
          message: res?.data?.message,
        });
        setInputMode(null);
      }
    }
  };

  useEffect(() => {
    if (inputMode === "audio") {
      trackZymedEvent(ZYMED_EVENTS.DDX_SYMPTOM_INPUT_STARTED, { input_mode: "voice" });
      startRecording();
    } else if (inputMode === "converting") {
      // "Processing ..." is on screen from the moment the mode flips, whether or not
      // the blob has been handed over yet.
      trackZymedEvent(ZYMED_EVENTS.DDX_SYMPTOM_PROCESSING);
      if (!!audioBlob) convertAudioToText();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMode, audioBlob]);

  useEffect(() => {
    if (isEditMode) setTempValue(value);
  }, [isEditMode]);

  const handleTextareaFocus = () => {
    // Handle iOS WebView rendering issues without breaking web functionality
    if (textareaRef.current) {
      // Force redraw for iOS WebView
      const textarea = textareaRef.current;
      const currentValue = textarea.value;
      setTimeout(() => {
        textarea.value = "";
        textarea.value = currentValue;
        textarea.focus();
      }, 0);
    }
  };

  const renderContent = () => {
    switch (inputMode) {
      case "converting":
        return (
          <div className="sticky bottom-0 p-5 bg-white flex flex-col gap-8 border-t-[0.5px] border-t-[#E2E2EA]">
            <div
              className="border-[#4B4AD5] rounded-2xl flex flex-col gap-2.5 p-3"
              style={{ borderWidth: "1px" }}
            >
              <ShimmerLine />
              <ShimmerLine />
              <ShimmerLine />
              <ShimmerLine />
              <ShimmerLine />
              <p className="font-normal text-center text-sm text-[#3D485499]">
                Processing ...
              </p>
            </div>
          </div>
        );
      case "audio":
        return (
          <div className="sticky bottom-0 p-5 bg-white flex flex-col gap-8 border-t-[0.5px] border-t-[#E2E2EA]">
            <div
              className="border-[#4B4AD5] rounded-2xl flex flex-col gap-5 py-6 px-3"
              style={{ borderWidth: "1px" }}
            >
              <div className="flex flex-row items-center">
                <p className="text-[#454551] text-xs text-left">
                  {formatTime(timer)}
                </p>
                <div className="ml-2 mr-3 h-6 w-full">
                  <VoiceWaveVisualizer
                    isRecording={isRecording}
                    isPaused={isPaused}
                  />
                </div>
                <div className="flex flex-row items-center gap-4">
                  <button onClick={togglePause}>
                    {isPaused ? (
                      <Resume className="min-h-8 min-w-8" />
                    ) : (
                      <Pause className="min-h-8 min-w-8" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      trackZymedEvent(
                        ZYMED_EVENTS.DDX_SYMPTOM_VOICE_RECORDING_SUBMITTED,
                        { recording_duration_sec: timer }
                      );
                      stopRecording();
                      setInputMode("converting");
                    }}
                  >
                    <Accept className="min-h-8 min-w-8" />
                  </button>
                </div>
              </div>
              <div className="flex flex-row items-center justify-between">
                <button
                  onClick={() => {
                    trackZymedEvent(
                      ZYMED_EVENTS.DDX_SYMPTOM_VOICE_RECORDING_DELETED,
                      { recording_duration_sec_at_delete: timer }
                    );
                    stopRecording();
                    setTimer(0);
                    setInputMode(null);
                  }}
                >
                  <Delete className="min-h-6 min-w-6" />
                </button>
                {isPaused ? (
                  <div className="flex flex-row items-center gap-0.5">
                    <p className="font-normal text-center text-sm text-[#3D485499]">
                      Tap on the mic
                    </p>
                    <MicOutline className="min-h-3 min-w-3" />
                    <p className="font-normal text-center text-sm text-[#3D485499]">
                      to continue recording!
                    </p>
                  </div>
                ) : (
                  <p className="font-normal text-center text-sm text-[#3D485499]">
                    Listening ...
                  </p>
                )}
                <div />
              </div>
            </div>
          </div>
        );
      case "text":
        return (
          <div className="sticky bottom-0 p-5 bg-white flex flex-col gap-8 border-t-[0.5px] border-t-[#E2E2EA]">
            <div
              className="border-[#4B4AD5] rounded-2xl flex flex-col overflow-hidden"
              style={{ borderWidth: "1px" }}
            >
              {isEditMode && (
                <div className="bg-[#F1F1F5] flex flex-row items-center justify-between py-2 px-4">
                  <p className="text-[#A2A2A8] text-left font-normal text-sm">
                    Edit Message
                  </p>
                  <button
                    onClick={() => {
                      if (tempValue !== value)
                        editConfirmationSheetRef?.current?.show();
                      else cancelEditMode();
                    }}
                  >
                    <CloseOutline className="min-h-4 min-w-4" />
                  </button>
                </div>
              )}
              <div className="border-[#4B4AD5] rounded-2xl flex flex-col gap-4 py-3 px-4">
                <textarea
                  className="w-full border-none outline-none bg-white resize-none text-[#454551] text-sm font-normal placeholder-[#98A2B3] placeholder-text-sm placeholder-font-normal"
                  style={
                    {
                      caretColor: "#4B4AD5",
                      WebkitTextFillColor: "#454551",
                      opacity: 1,
                      backgroundColor: "white",
                    } as React.CSSProperties
                  }
                  rows={4}
                  value={value}
                  onChange={(e) => {
                    if (!value) {
                      trackZymedEvent(ZYMED_EVENTS.DDX_SYMPTOM_INPUT_STARTED, {
                        input_mode: "text",
                      });
                    }
                    setValue(e.target.value);
                  }}
                  placeholder="Type your symptoms & more here"
                  ref={textareaRef}
                  onFocus={handleTextareaFocus}
                />
                <div className="flex flex-row items-center justify-between">
                  {/* <button onClick={() => fileSheetRef.current?.show()}>
                  <Add className="min-h-7 min-w-7" />
                </button> */}
                  <div />
                  {value?.length > 0 ? (
                    <div className="flex flex-row items-center gap-4">
                      <button onClick={() => setInputMode("audio")}>
                        <Mic className="min-h-7 min-w-7" />
                      </button>
                      <button
                        onClick={() => {
                          if (tempValue !== value) onPressSend(null);
                          else cancelEditMode();
                        }}
                      >
                        <Lottie
                          animationData={DDxSend}
                          autoplay
                          loop
                          className="h-7 w-7"
                        />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setInputMode("audio")}>
                      <Lottie
                        animationData={DDxMic}
                        loop
                        autoplay
                        autoPlay
                        className="h-7 w-7"
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>
            {/* <BottomSheetWrapper ref={fileSheetRef}>
              <div className="px-6 py-3 flex flex-row items-center justify-between">
                <p className="text-[#1F2933] text-xl text-left">Upload</p>
                <button onClick={() => fileSheetRef.current?.hide()}>
                  <Close className="min-h-6 min-w-6" />
                </button>
              </div>
              <div className="pt-2 pb-1">
                <button className="w-full flex flex-row items-center justify-between py-2 pl-4 pr-6 gap-4">
                  <Camera className="min-h-10 min-w-10" />
                  <p className="w-full font-normal text-[#1D1B20] text-base text-left">
                    Use Camera
                  </p>
                  <ChevronRight className="min-h-6 min-w-6" />
                </button>
                <button className="w-full flex flex-row items-center justify-between py-2 pl-4 pr-6 gap-4">
                  <Gallery className="min-h-10 min-w-10" />
                  <p className="w-full font-normal text-[#1D1B20] text-base text-left">
                    Upload from Gallery
                  </p>
                  <ChevronRight className="min-h-6 min-w-6" />
                </button>
                <button className="w-full flex flex-row items-center justify-between py-2 pl-4 pr-6 gap-4">
                  <Files className="min-h-10 min-w-10" />
                  <p className="w-full font-normal text-[#1D1B20] text-base text-left">
                    Upload from files
                  </p>
                  <ChevronRight className="min-h-6 min-w-6" />
                </button>
              </div>
            </BottomSheetWrapper> */}
            <BottomSheetWrapper ref={editConfirmationSheetRef}>
              <div className="p-6 flex flex-col gap-4">
                <div className="flex flex-row items-start gap-3">
                  <p className="text-[#1A1C1E] text-xl font-semibold text-left">
                    Are you sure you want to discard your changes?
                  </p>
                  <button
                    onClick={() => editConfirmationSheetRef.current?.hide()}
                  >
                    <Close className="min-h-6 min-w-6" />
                  </button>
                </div>
                <p className="text-[#3D4854] font-normal text-base text-left">
                  The edits you made will not be saved if you close this
                  message. Do you want to proceed?
                </p>
                <div className="flex flex-row gap-4 items-center">
                  <button
                    onClick={() => editConfirmationSheetRef.current?.hide()}
                    style={{ borderWidth: "1px" }}
                    className="overflow-hidden gap-2 self-stretch px-4 py-4 mt-6 w-full text-lg font-medium leading-none text-center bg-white text-indigo-600 border-indigo-600 rounded-2xl min-h-[56px]"
                  >
                    Cancel
                  </button>
                  <Button label="Discard Changes" onClick={cancelEditMode} />
                </div>
              </div>
            </BottomSheetWrapper>
          </div>
        );
      case null:
        return (
          <div className="sticky bottom-0 p-5 bg-white flex flex-col gap-8 border-t-[0.5px] border-t-[#E2E2EA]">
            <button
              className="flex flex-col items-center gap-2.5"
              onClick={() => setInputMode("audio")}
            >
              <div className="relative h-[90px] w-[90px] overflow-hidden rounded-full">
                <img
                  src={DDxInputSelectorBg}
                  alt="bg"
                  className="h-full w-full opacity-80"
                />
                <MicWhiteFilled className="min-w-5 min-h-5 absolute z-10 top-[35px] left-[35px]" />
                <Lottie
                  animationData={Chakra}
                  className="h-20 w-20 absolute z-10 top-[5px] left-[5px]"
                  autoplay
                  autoPlay
                  loop
                />
              </div>
              <p className="text-[#454551] font-normal text-sm">
                Tap to Speak the symptom & more
              </p>
            </button>
            <button
              onClick={() => setInputMode("text")}
              className="bg-white border-[#D0D5DD] rounded-xl p-4 text-[#98A2B3] text-left font-normal text-sm"
              style={{ borderWidth: "1px" }}
            >
              or type your symptoms & more here
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return renderContent();
};

export default DDxInputField;
